// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment } from 'react';
import Dropzone from 'react-dropzone';
import { PropTypes } from 'prop-types';
import { makeStyles, FormHelperText, Typography } from '@material-ui/core';
import PublishRoundedIcon from '@material-ui/icons/PublishRounded';
import clsx from 'clsx';

import ListFilesRaw from './ListFiles';
import styles from './styles';
import { getI18nFormattedMessage } from "../../utils/intl";

export default function FileDropzone(props) {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const {
    handleFiles,
    allowedFileExtentions,
    listFileTitles = ['Selected files'],
    onRemoveFile,
    isValidation,
    validationMessage,
    id,
    defaultUploaderText = 'Uploader',
    defaultSelectedText = 'No files selected.',
    isMultipleTitle = false,
    title,
    showSelectedFiles = true,
    selectedFiles,
    labelClass = '',
    showFilesHeader= false,
    filesHeaderTitleClass,
    filesHeaderNoFileLabelClass,
  } = props;

  return (
    <div id={id} data-apim-test={id}>
      <h4 className={clsx(classes.formLabel, labelClass)}>{title}</h4>
      <Fragment>
        <Dropzone
          accept={selectedFiles.acceptFileTypes}
          maxSize={selectedFiles.maxSize}
          maxFiles={selectedFiles.maxFiles}
          onDrop={acceptedFiles => handleFiles(acceptedFiles)}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()} className={classes.container}>
              <input {...getInputProps()} />
              <div className={classes.fileLabel}>
                <PublishRoundedIcon color="primary" fontSize="large" />
                <div>
                  {defaultUploaderText}
                </div>
              </div>
            </div>
          )}
        </Dropzone>
        <FormHelperText
          classes={{
            root: classes.root,
          }}
          error={selectedFiles.error}
        >
          <div className={classes.helperText}>
            {selectedFiles.helperText}
          </div>
        </FormHelperText>
      </Fragment>
      {isValidation &&
        <div className={classes.error}>{validationMessage}</div>
      }
      {
          showFilesHeader && (
            <div className={classes.selectedFileContainer} data-apim-test={`${id}-files-header`}>
              <Typography className={clsx(classes.formLabel, filesHeaderTitleClass)}>
                {getI18nFormattedMessage('label.filedropzone.files.header.title')}
              </Typography>
              { selectedFiles.files.length === 0 &&
                  <Typography className={clsx(classes.helperText, filesHeaderNoFileLabelClass)}>
                    {getI18nFormattedMessage('label.filedropzone.files.header.no.files.uploaded')}
                  </Typography>
              }
            </div>
          )
      }
      {showSelectedFiles && (
        isMultipleTitle ?
          allowedFileExtentions.map((value, index) => {
            const filterFiles = selectedFiles.files.filter((file) => file.ext === value);
            return (
              <ListFilesRaw
                title={listFileTitles[index] ? listFileTitles[index] : listFileTitles[0]}
                files={filterFiles}
                defaultText={defaultSelectedText}
                onRemoveFile={onRemoveFile}
                id={`selected-container-${index.toString()}`}
                key={`file_${index.toString()}`}
                labelClass={labelClass}
              />
            );
          })
        :
          <ListFilesRaw
            title={listFileTitles[0]}
            files={selectedFiles.files}
            onRemoveFile={onRemoveFile}
            defaultText={defaultSelectedText}
            id={'file-selected-container'}
            labelClass={labelClass}
          />
        )
      }
    </div>
  );
}

FileDropzone.propTypes = {
  showSelectedFiles: PropTypes.bool,
  selectedFiles: PropTypes.object,
  handleFiles: PropTypes.func,
  allowedFileExtentions: PropTypes.arrayOf(PropTypes.string),
  listFileTitles: PropTypes.arrayOf(PropTypes.string),
  onRemoveFile: PropTypes.func,
  isValidation: PropTypes.bool,
  validationMessage: PropTypes.string,
  id: PropTypes.string,
  defaultUploaderText: PropTypes.string,
  defaultSelectedText: PropTypes.string,
  isMultipleTitle: PropTypes.bool,
  title: PropTypes.string,
  labelClass: PropTypes.object,
  showFilesHeader : PropTypes.bool,
  filesHeaderTitleClass : PropTypes.object,
  filesHeaderNoFileLabelClass : PropTypes.object,
};
